import React, { useEffect, useState } from "react";
import "./TimelinePage.css";
import { renderRichText } from "../../utils/richTextUtils";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fetchTimelineProjects = async (apiToken) => {
  let allProjects = [];
  let page = 1;
  const pageSize = 200;
  let totalPages = 1;

  while (page <= totalPages) {
    const response = await fetch(
      `${API_BASE_URL}/api/single-projects?populate=FeaturedImage&filters[IncludeTimeline][$eq]=true&pagination[page]=${page}&pagination[pageSize]=${pageSize}`,
      {
        headers: {
          Authorization: `Bearer ${apiToken}`,
        },
      }
    );
    const data = await response.json();
    allProjects = [...allProjects, ...data.data];
    totalPages = data.meta.pagination.pageCount;
    page++;
  }

  return allProjects.sort(
    (a, b) => new Date(b.attributes.Date) - new Date(a.attributes.Date)
  );
};

const TimelinePage = ({
  apiToken,
  onLongNavigate,
  setSliderTheme,
  setIsHeaderVisible,
}) => {
  const [hoveredProject, setHoveredProject] = useState(null);
  const [clickedProject, setClickedProject] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { data: projects = [], isLoading, error } = useQuery({
    queryKey: ["timeline-projects"],
    queryFn: () => fetchTimelineProjects(apiToken),
    staleTime: 1000 * 60 * 60 * 24, // 24h
    cacheTime: 1000 * 60 * 60 * 24, // 24h em cache
  });

  useEffect(() => {
    setIsHeaderVisible(true);
    setSliderTheme("Light");
  }, [setIsHeaderVisible, setSliderTheme]);

  const handleItemClick = (hasSinglePage, slug) => {
    if (slug) {
      setClickedProject(slug);
      setIsTransitioning(true);
      if (hasSinglePage) {
        onLongNavigate(`/projects/${slug}`);
      }
    }
  };

  const handleMouseEnter = (project) => {
    if (
      !isTransitioning &&
      project.attributes.FeaturedImage &&
      project.attributes.FeaturedImage.data
    ) {
      setHoveredProject(project.attributes.FeaturedImage.data.attributes.url);
    }
  };

  const handleMouseLeave = () => {
    if (!isTransitioning) {
      setHoveredProject(null);
    }
  };

  if (isLoading) return <div></div>;
  if (error) return <div>Erro ao carregar timeline.</div>;

  const ogImage = "https://productive-prosperity-58dbc90efe.media.strapiapp.com/SEINEDESIGN_hotel_flottant_paris_off_1_3296f3d699.jpg"
  const ogUrl = typeof window !== "undefined" ? window.location.href : "https://ronzatti.com/timeline";

  return (
    <>
    <Helmet>
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={ogUrl} />
        <link rel="canonical" href={ogUrl} />
        <title>
          Histoire et Évolution - Timeline Seine Design Architecture
        </title>
        <meta
          name="description"
          content="Retracez l'histoire et les étapes clés de Seine Design. Trente ans d'innovation en architecture flottante."
        />
        <meta
          property="og:title"
          content="Histoire et Évolution - Timeline Seine Design Architecture"
        />
        <meta
          property="og:description"
          content="Découvrez l'histoire de Seine Design et ses étapes clés. Suivez notre parcours de trente ans en architecture flottante, de nos débuts aux innovations actuelles, et comprenez notre vision."
        />
        <meta
          property="og:image"
          content={ogImage}
        />
      </Helmet>
    <div className={`timeline-container ${hoveredProject ? "isHovering" : ""}`}>
      <div className="timeline-list">
        {projects.map((project, index) => {
          const { slug, HasSinglePage } = project.attributes;
          return (
            <div
              key={project.id}
              className={`timeline-item ${
                clickedProject === slug && slug ? "hasClicked" : ""
              } ${HasSinglePage && slug ? "hasSinglePage" : ""}`}
              style={{ "--i": index + 1 }}
              onMouseEnter={() => handleMouseEnter(project)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleItemClick(HasSinglePage, slug)}
            >
              <div className="timeline-left">
                <p className="timeline-city">{project.attributes.City}</p>
                <p className="timeline-location">{project.attributes.Location}</p>
              </div>
              <div className="timeline-plus" style={{ "--i": index + 1 }}>+</div>
              <div className="timeline-right">
                <div className="timeline-title-container">
                  <h3 className="timeline-title">{project.attributes.Title}</h3>
                  <div className="timeline-date-status">
                    <p className="timeline-date">
                      {new Date(project.attributes.Date).getFullYear()}
                    </p>
                    <p className="timeline-status">{project.attributes.State}</p>
                  </div>
                </div>
                <p className="timeline-description">
                  {project.attributes.ShortDescription
                    ? renderRichText(project.attributes.ShortDescription)
                    : ""}
                </p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="timeline-image-container">
        <div className="block-anim"></div>
        {hoveredProject && (
          <img
            src={`${hoveredProject}`}
            alt="Hovered Project"
            className="timeline-image"
          />
        )}
      </div>
    </div>
  </>
  );
};

export default TimelinePage;
